const KEY_PATTERN = /^[a-zA-Z_][a-zA-Z0-9_]*$/;

let nextRowId = 1;

export function createConfigFieldRow(overrides = {}) {
  return {
    rowId: overrides.rowId ?? `cfg-${nextRowId++}`,
    key: overrides.key ?? '',
    title: overrides.title ?? '',
    description: overrides.description ?? '',
    required: Boolean(overrides.required),
    secret: Boolean(overrides.secret),
    markdown: Boolean(overrides.markdown)
  };
}

/**
 * @param {object|null|undefined} schema
 * @returns {Array}
 */
export function schemaToConfigFields(schema) {
  if (!schema || typeof schema !== 'object') return [];
  const props = schema.properties;
  if (!props || typeof props !== 'object') return [];
  const required = new Set(Array.isArray(schema.required) ? schema.required : []);

  return Object.entries(props).map(([key, def]) => createConfigFieldRow({
    key,
    title: def?.title || '',
    description: def?.description || '',
    required: required.has(key),
    secret: def?.secret === true,
    markdown: def?.format === 'markdown'
  }));
}

function isBlankRow(field) {
  return !field.key?.trim() && !field.title?.trim() && !field.description?.trim()
    && !field.required && !field.secret && !field.markdown;
}

/**
 * @param {Array} fields
 * @returns {{ valid: boolean, error?: string }}
 */
export function validateConfigFields(fields) {
  const rows = Array.isArray(fields) ? fields : [];
  const activeRows = rows.filter((row) => !isBlankRow(row));

  if (activeRows.length === 0) {
    return { valid: true };
  }

  const seenKeys = new Set();

  for (let i = 0; i < activeRows.length; i += 1) {
    const row = activeRows[i];
    const key = String(row.key || '').trim();
    const title = String(row.title || '').trim();
    const rowLabel = `Variable ${i + 1}`;

    if (!key) {
      return { valid: false, error: `${rowLabel}: variable name is required` };
    }
    if (!KEY_PATTERN.test(key)) {
      return { valid: false, error: `${rowLabel}: variable name must start with a letter or underscore and contain only letters, numbers, and underscores` };
    }
    if (!title) {
      return { valid: false, error: `${rowLabel}: label is required` };
    }
    if (row.secret && row.markdown) {
      return { valid: false, error: `${rowLabel}: a field cannot be both secret and markdown` };
    }
    if (seenKeys.has(key)) {
      return { valid: false, error: `Duplicate variable name: ${key}` };
    }
    seenKeys.add(key);
  }

  return { valid: true };
}

/**
 * @param {Array} fields
 * @returns {object|null}
 */
export function configFieldsToSchema(fields) {
  const rows = Array.isArray(fields) ? fields : [];
  const activeRows = rows.filter((row) => !isBlankRow(row));

  if (activeRows.length === 0) {
    return null;
  }

  const validation = validateConfigFields(activeRows);
  if (!validation.valid) {
    throw new Error(validation.error);
  }

  const properties = {};
  const required = [];

  for (const row of activeRows) {
    const key = row.key.trim();
    const prop = { type: 'string', title: row.title.trim() };
    const description = String(row.description || '').trim();
    if (description) {
      prop.description = description;
    }
    if (row.secret) {
      prop.secret = true;
    }
    if (row.markdown) {
      prop.format = 'markdown';
    }
    properties[key] = prop;
    if (row.required) {
      required.push(key);
    }
  }

  const schema = { type: 'object', properties };
  if (required.length) {
    schema.required = required;
  }
  return schema;
}
